import { FileViewModel, Shell, type FileViewState } from "@bundle/core";
import type { FileRef } from "@bundle/contracts";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import type { FileActions } from "../components/MessageFiles.tsx";
import { useNavigation, useWorkspaceContext } from "../context/AppContext.tsx";
import { useFilePlatform } from "../context/FilesContext.tsx";
import { useOwned } from "./owned.ts";

/** What a message's files can do here: download (when allowed), copy the file page link, open the file page. */
export function useFileActions(canDownload: boolean): FileActions {
  const platform = useFilePlatform();
  const nav = useNavigation();
  const { slug } = useWorkspaceContext();
  return useMemo(() => {
    const page = (f: FileRef) => Shell.formatUrl({ type: "file", ws: slug, fileId: f.id });
    return {
      canDownload,
      onDownload: (f: FileRef) => platform.download(f),
      onCopyLink: (f: FileRef) => platform.copyLink(page(f)),
      onDetails: (f: FileRef) => nav.open(page(f), nav.canSplit ? "split" : "current"),
    };
  }, [canDownload, platform, nav, slug]);
}

/** The view model of a file's page, shared while shown and disposed when unused. */
export function useFile(fileId: string): { vm: FileViewModel; state: FileViewState } {
  const { workspace } = useWorkspaceContext();
  const key = `file:${workspace.wsId}:${workspace.state.meId}:${fileId}`;
  const create = useCallback(() => new FileViewModel(workspace, fileId), [workspace, fileId]);
  const vm = useOwned(key, create);
  const state = useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot);
  return { vm, state };
}
